<template>
  <div class="despesas-status-page">
    <div class="page-container">
      <div class="page-header">
        <h3 class="page-title">{{ t('Status das despesas') }}</h3>
        <div class="page-actions">
          <Button
            :label="t('Novo Status')"
            icon="pi pi-plus"
            data-testid="despesas-status-btn-novo"
            @click="handleCreate"
          />
        </div>
      </div>
      <div class="page-body">
        <DespesasStatusTable
          @edit="handleEdit"
          @delete="handleDelete"
        />
      </div>
    </div>

    <!-- DespesasStatusModal is centralized in ModalManager.vue and opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, provide } from 'vue';
import { useI18n } from 'vue-i18n';
import DespesasStatusTable from './DespesasStatusTable.vue';
import { useDespesasStatus } from '@/composables/useDespesasStatus';
import { useConfirmAction } from '@/composables/useConfirmAction';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface DespesaStatus {
  despesa_status_id: Id;
  despesa_status_titulo?: string;
  [key: string]: unknown;
}

const { t } = useI18n();

const props = defineProps<{
  translations: Record<string, unknown>;
}>();

provide('translations', props.translations);

const { deleteStatus, fetchStatusById } = useDespesasStatus();
const { confirmAction } = useConfirmAction();

const handleCreate = (): void => {
  log('DespesasStatusPage', 'handleCreate', 'Opening create modal via eventBus');
  eventBus.emit('modal.despesas-status.open', { mode: 'create', status: null });
};

const handleEdit = async (statusId: Id): Promise<void> => {
  log('DespesasStatusPage', 'handleEdit', 'Opening edit modal for ID via eventBus', statusId);

  try {
    const status = await fetchStatusById(statusId) as DespesaStatus | null;

    if (status) {
      log('DespesasStatusPage', 'handleEdit', 'Found status, opening modal', status);
      eventBus.emit('modal.despesas-status.open', { mode: 'edit', status });
    } else {
      log('DespesasStatusPage', 'handleEdit', 'Status not found', { statusId });
      eventBus.emit('globalMessage.warn', t('Status não encontrado'));
    }
  } catch (error) {
    log('DespesasStatusPage', 'handleEdit', 'Error fetching status', error);
    const detail = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar status') + ': ' + detail);
  }
};

const handleDelete = (status: DespesaStatus): void => {
  log('DespesasStatusPage', 'handleDelete', 'Delete requested', status);

  confirmAction({
    message: t('Tem certeza que deseja excluir este status?'),
    header: t('Confirmação'),
    onAccept: async () => {
      try {
        await deleteStatus(status.despesa_status_id);
        eventBus.emit('globalMessage.success', t('Status excluído com sucesso'));
        eventBus.emit('despesas-status.refresh');
      } catch (error) {
        log('DespesasStatusPage', 'handleDelete', 'Error deleting status', error);
        const detail = error instanceof Error ? error.message : String(error);
        eventBus.emit('globalMessage.error', t('Erro ao excluir status') + ': ' + detail);
      }
    }
  });
};

const handleStatusSaved = (): void => {
  log('DespesasStatusPage', 'handleStatusSaved', 'Status saved');
  eventBus.emit('despesas-status.refresh');
};

onMounted(() => {
  log('DespesasStatusPage', 'onMounted', 'Page mounted');
  eventBus.on('despesas-status.saved', handleStatusSaved);
});

onBeforeUnmount(() => {
  log('DespesasStatusPage', 'onBeforeUnmount', 'Cleaning up event listeners');
  eventBus.off('despesas-status.saved', handleStatusSaved);
});
</script>

<style scoped>
.despesas-status-page {
  padding: 0;
}

.page-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.page-header {
  padding: 15px 20px;
  border-bottom: 1px solid #f4f4f4;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.page-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #333;
}

.page-actions {
  display: flex;
  gap: 10px;
}

.page-body {
  padding: 20px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .page-actions {
    width: 100%;
  }

  .page-actions :deep(.p-button) {
    width: 100%;
  }
}
</style>
